/**
 * P2-WORKSPACE D8 — live interview path (headless loader + state machine).
 *
 * Deliverable 8: "Exercise complete live interview path: realistic latency +
 * failure states; loading/stale/error/no-match states."
 *
 * This module TYPES the states and owns the data flow; the UI consumes a
 * single InterviewState. Rules:
 * - The interview payload = customer + their orders + their SkierDNA records
 *   (salesQuestionAnswer per order — the P0-DNA contract).
 * - Failure states are FIRST-CLASS: 'loading' | 'ready' | 'error' |
 *   'no-match' | 'stale' — never a silent empty.
 * - Staleness: a snapshot older than the TTL is marked 'stale' with the
 *   fetchedAt timestamp surfaced (designer sees the data's age).
 * - Deterministic given the same fetched payload (pure reducers).
 * - PII discipline: this module passes through what the API returns; the
 *   workspace displays emails/phones only to signed-in Wagner staff (the
 *   API's Google-only gate is the boundary — SEC-1).
 */

export const INTERVIEW_LOADER_VERSION = '0.1';

/** Staleness TTL: 10 minutes (pilot conversations are live). */
export const STALE_TTL_MS = 10 * 60 * 1000;

export interface InterviewCustomer {
  id: number;
  name: string;
  email: string | null;
  phone: string | null;
  lifecycleStatus?: string | null;
}

export interface InterviewOrderSummary {
  orderId: number;
  name: string;
  serial: number | null;
  skiType: string | null;
  lengthCm: number | null;
  state: string | null;
}

export interface InterviewDnaSummary {
  orderId: number;
  answerCount: number;
  /** Truncated preview of the latest answers (for the designer's context). */
  latestAnswers: string[];
}

export interface InterviewPayload {
  fetchedAtMs: number;
  customer: InterviewCustomer;
  orders: InterviewOrderSummary[];
  dna: InterviewDnaSummary[];
}

export type InterviewStatus = 'idle' | 'loading' | 'ready' | 'error' | 'no-match' | 'stale';

export interface InterviewState {
  status: InterviewStatus;
  /** Present when status = ready/stale. */
  payload?: InterviewPayload;
  /** Present when status = error. */
  errorMessage?: string;
  /** Present when status = stale. */
  staleForMs?: number;
}

export function idleInterview(): InterviewState {
  return { status: 'idle' };
}

export function loadingInterview(): InterviewState {
  return { status: 'loading' };
}

export function errorInterview(message: string): InterviewState {
  return { status: 'error', errorMessage: message };
}

/** No-match: customer resolved but zero orders AND zero DNA records. */
export function noMatchInterview(): InterviewState {
  return { status: 'no-match' };
}

/** Ready or stale depending on the payload's age (single source of truth). */
export function readyInterview(payload: InterviewPayload, nowMs: number): InterviewState {
  const age = nowMs - payload.fetchedAtMs;
  if (age > STALE_TTL_MS) {
    return { status: 'stale', payload, staleForMs: age };
  }
  return { status: 'ready', payload };
}

/**
 * Reduce raw API responses into the interview payload.
 * Pure — the caller does the fetching; this owns interpretation.
 * Memory note applied: interview answers live in `salesQuestionAnswer`
 * on the order (NOT skierdnaAnswer, which is always empty on prod).
 */
export function buildInterviewPayload(input: {
  nowMs: number;
  customer: Record<string, unknown>;
  orders: Array<Record<string, unknown>>;
  orderForms: Record<number, Record<string, unknown>>;
  salesAnswers: Record<number, Array<Record<string, unknown>>>;
}): InterviewPayload {
  const c = input.customer;
  const orders: InterviewOrderSummary[] = [];
  const dna: InterviewDnaSummary[] = [];

  for (const o of input.orders) {
    const orderId = Number(o.id);
    const form = input.orderForms[orderId];
    const lengthMm = form ? Number(form.length) : NaN;
    orders.push({
      orderId,
      name: String(o.name ?? ''),
      serial: o.serial ? Number(o.serial) : null,
      skiType: form ? String(form.skiType ?? '') || null : null,
      lengthCm: Number.isFinite(lengthMm) ? Math.round(lengthMm / 10) : null,
      state: String((o.orderState as { name?: string } | undefined)?.name ?? o.stateId ?? '') || null,
    });
    const answers = input.salesAnswers[orderId] ?? [];
    if (answers.length > 0) {
      dna.push({
        orderId,
        answerCount: answers.length,
        latestAnswers: answers
          .slice(0, 3)
          .map((a) => String(a.answer ?? '').slice(0, 160)),
      });
    }
  }

  return {
    fetchedAtMs: input.nowMs,
    customer: {
      id: Number(c.id),
      name: [c.firstName, c.lastName].filter(Boolean).join(' ') || String((c as Record<string, unknown>).name ?? `Customer ${c.id}`),
      email: (c.email as string) ?? null,
      phone: (c.phone as string) ?? null,
      lifecycleStatus: (c.lifecycleStatus as string) ?? null,
    },
    orders,
    dna,
  };
}

/** Whether an interview payload can seed a design session (has usable signal). */
export function interviewHasSignal(payload: InterviewPayload): boolean {
  return payload.orders.some((o) => o.lengthCm !== null) || payload.dna.length > 0;
}